/**
 * Bausteine des Mail-Fensters (Mockup V4, docs/KONZEPT-GESPRAECH-MAILFENSTER.md Punkte 2–3):
 *  - `MailListenZeile`  — Liste wie Apple Mail: Avatar, Name, Uhrzeit, Betreff, zwei Zeilen Vorschau,
 *                         Zustellstatus in Worten, ungelesen = Punkt;
 *  - `MailNachricht`    — eine geoeffnete Nachricht: Kopf (Avatar, Name + GAST/DU, Adresse, „An:“,
 *                         Datum), Inhalt, Anhang-Chips, Status, Aktionen „KI-Entwurf“/„Antworten“;
 *  - `MailKurzZeile`    — aeltere Nachricht als einzeilige Zeile zum Aufklappen;
 *  - Zuordnungs-Werkzeuge (Chip + „⋯“-Menue) wie in der bisherigen Karte, unveraendert wiederverwendet.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Icon, MailFehlerHinweis } from "../components";
import { attachmentIcon, formatBytes, formatMailEreignisse } from "../format";
import { ZuordnungAendernModal, ZuordnungAufhebenModal, ZuordnungsChip } from "../ZuordnungControls";
import { dekodiereMailBetreff, entferneMailClientArtefakte } from "../../lib/mail-anzeige";
import { gliedereGespraechNachricht, sendezeitpunkt } from "../../lib/gespraech";
import {
  adresseVon,
  anzeigeNameVon,
  initialen,
  kurzesDatum,
  langesDatum,
  mailStatus,
  uhrzeit,
  vorschau,
  type MailStatus,
  type StatusTon,
} from "../../lib/gespraech-mailfenster";
import { GespraechMailKoerper } from "./GespraechMailKoerper";
import { SYNTHETISCHE_NACHRICHT_ID, type GespraechAttachment, type GespraechEmailRow } from "./gespraech-typen";

/** Farben der Zustellstatus-Zeilen (Mockup V4: gruen/rot/gelb). Gruen und Rot nach Designregel
 *  „Erfolg ist gruen, Fehler sind rot“; das Gelb von „Unbeantwortet“ steht NUR hier. */
const TON_KLASSE: Record<StatusTon, string> = {
  ok: "text-tertiary dark:text-tertiary-fixed-dim",
  err: "text-error",
  amb: "text-[#8A5A00] dark:text-[#F2C56B]",
  neutral: "text-secondary dark:text-secondary-fixed-dim",
};

export interface MailKontext {
  kundenName: string | null;
  kundenFirma: string | null;
  firmaName: string | null;
  eventId: string;
}

/** Wer ist die „Absenderseite“ dieser Zeile? gast = der Kunde, wir = der Betrieb. */
function seite(email: GespraechEmailRow): "gast" | "wir" {
  return email.direction === "inbound" ? "gast" : "wir";
}

function anzeigeName(email: GespraechEmailRow, k: MailKontext): string {
  if (email.direction === "inbound") return k.kundenName?.trim() || anzeigeNameVon(email.fromAddr) || email.fromAddr;
  return k.firmaName?.trim() || "Wir";
}

function Avatar({ name, art, klein }: { name: string; art: "gast" | "wir"; klein?: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={`grid shrink-0 place-items-center rounded-full font-extrabold text-white ${klein ? "h-[34px] w-[34px] text-[13px]" : "h-[42px] w-[42px] text-[15px]"} ${
        art === "gast" ? "bg-slate-600 dark:bg-slate-500" : "bg-primary"
      }`}
    >
      {initialen(name)}
    </span>
  );
}

export function WhatsappKennzeichen() {
  return (
    <span className="inline-flex items-center gap-0.5 rounded-full bg-[#F0EAE0] px-1.5 py-0 font-label-md text-[10px] font-semibold text-tertiary dark:bg-white/[0.08] dark:text-[#b8ae9e]">
      <Icon name="chat" className="text-[10px]" />
      WhatsApp
    </span>
  );
}

/** Anhang als Chip (Mockup: Symbol + Dateiname + Groesse). */
function AnhangChip({ a }: { a: GespraechAttachment }) {
  return (
    <span className="inline-flex max-w-full items-center gap-2 rounded-[10px] border border-outline-variant/40 bg-surface-container-lowest px-2.5 py-2 font-body-sm text-[13.5px] dark:border-outline/30 dark:bg-white/[0.03]">
      <Icon name={attachmentIcon(a.mimeType)} className="text-[20px] text-secondary dark:text-secondary-fixed-dim" />
      <span className="min-w-0 truncate">{a.filename ?? "Anhang"}</span>
      <span className="shrink-0 text-secondary/80 dark:text-secondary-fixed-dim/80">{formatBytes(a.sizeBytes)}</span>
    </span>
  );
}

/** „⋯“-Menue einer eingehenden Nachricht — „Zuordnung ändern…“/„Zuordnung aufheben“. Klick ausserhalb
 *  ODER Escape schliesst (wie `BubbleMenu` in `pages/posteingang.tsx`). */
function NachrichtMenu({ onAendern, onAufheben }: { onAendern: () => void; onAufheben: () => void }) {
  const [offen, setOffen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!offen) return;
    const aussen = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOffen(false);
    };
    const escape = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOffen(false);
    };
    document.addEventListener("mousedown", aussen);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", aussen);
      document.removeEventListener("keydown", escape);
    };
  }, [offen]);
  return (
    <div ref={ref} className="relative shrink-0">
      <button
        type="button"
        aria-label="Weitere Aktionen"
        aria-haspopup="menu"
        aria-expanded={offen}
        onClick={() => setOffen((o) => !o)}
        className="rounded-md p-0.5 text-secondary/70 transition-colors hover:bg-surface-container-high hover:text-on-background dark:text-secondary-fixed-dim/70 dark:hover:bg-white/[0.06]"
      >
        <Icon name="more_horiz" className="text-[18px]" />
      </button>
      {offen && (
        <div
          role="menu"
          className="absolute bottom-full left-0 z-20 mb-1 w-48 overflow-hidden rounded-lg border border-outline-variant/40 bg-surface-container-lowest shadow-lg dark:border-outline/30 dark:bg-night-card"
        >
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOffen(false);
              onAendern();
            }}
            className="block w-full px-3 py-2 text-left font-body-sm text-[12.5px] text-on-background hover:bg-surface-container-high dark:text-inverse-on-surface dark:hover:bg-white/[0.05]"
          >
            Zuordnung ändern…
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOffen(false);
              onAufheben();
            }}
            className="block w-full px-3 py-2 text-left font-body-sm text-[12.5px] text-error hover:bg-surface-container-high dark:hover:bg-white/[0.05]"
          >
            Zuordnung aufheben
          </button>
        </div>
      )}
    </div>
  );
}

/** Zeile der Liste (Handy: eigene Seite, Desktop: linke Spalte). */
export function MailListenZeile({
  email,
  kontext,
  ausgewaehlt,
  unbeantwortet,
  onOeffnen,
}: {
  email: GespraechEmailRow;
  kontext: MailKontext;
  ausgewaehlt: boolean;
  /** Text „Unbeantwortet seit 4 Std.“ — nur an der juengsten, unbeantworteten Gast-Nachricht. */
  unbeantwortet: string | null;
  onOeffnen: () => void;
}) {
  const art = email.art === "betrieb" ? "wir" : seite(email);
  const name = anzeigeName(email, kontext);
  const betreff = dekodiereMailBetreff(email.subject)?.trim();
  const status: MailStatus | null = unbeantwortet ? { ton: "amb", text: unbeantwortet } : mailStatus(email);
  const ungelesen = email.direction === "inbound" && email.readAt === null;
  const empfaenger = email.empfaenger ?? [];
  const betriebAn = email.art === "betrieb" ? (empfaenger.length > 1 ? `An ${empfaenger.length} Adressen` : empfaenger[0] ? `An ${adresseVon(empfaenger[0])}` : null) : null;
  return (
    <button
      type="button"
      data-testid={`gespraech-zeile-${email.id}`}
      aria-current={ausgewaehlt ? "true" : undefined}
      onClick={onOeffnen}
      className={`flex w-full gap-3 border-b border-l-[3px] border-b-outline-variant/25 px-3.5 py-3 text-left transition-colors last:border-b-0 dark:border-b-outline/15 ${
        ausgewaehlt ? "border-l-primary bg-primary/[0.07] dark:bg-primary/[0.14]" : "border-l-transparent hover:bg-surface-container-high/50 dark:hover:bg-white/[0.03]"
      }`}
    >
      <Avatar name={name} art={art} />
      <span className="min-w-0 flex-1">
        <span className="flex items-baseline gap-2">
          <span className="min-w-0 flex-1 truncate font-body-sm text-[15.5px] font-bold text-on-background dark:text-inverse-on-surface">
            {ungelesen && <span aria-label="Ungelesen" className="mr-[7px] inline-block h-[9px] w-[9px] rounded-full bg-primary align-[1px]" />}
            {name}
          </span>
          <span className="shrink-0 font-body-sm text-[13px] text-secondary dark:text-secondary-fixed-dim">{uhrzeit(sendezeitpunkt(email))}</span>
        </span>
        {betreff && <span className="block truncate font-body-sm text-[14.5px] font-semibold text-on-background dark:text-inverse-on-surface">{betreff}</span>}
        <span className="line-clamp-2 font-body-sm text-[13.5px] leading-[1.4] text-secondary dark:text-secondary-fixed-dim">{vorschau(vorschauQuelle(email)) || "(Kein Text)"}</span>
        {email.channel === "whatsapp" && (
          <span className="mt-1 block">
            <WhatsappKennzeichen />
          </span>
        )}
        {(betriebAn || status) && (
          <span className={`mt-1 block font-body-sm text-[12.5px] font-semibold ${status ? TON_KLASSE[status.ton] : "text-secondary dark:text-secondary-fixed-dim"}`}>
            {[betriebAn, status?.text].filter(Boolean).join(" · ")}
          </span>
        )}
      </span>
    </button>
  );
}

/** Vorschau-Text: ohne Signatur und zitierte fruehere Nachricht (die stehen geoeffnet eingeklappt). */
function vorschauQuelle(email: GespraechEmailRow): string {
  const roh = entferneMailClientArtefakte(email.bodyText)?.trim() || stripHtml(email.bodyHtml);
  return gliedereGespraechNachricht(roh).sichtbar;
}

/** Nur fuer die Vorschau einer reinen HTML-Mail ohne Text-Teil (die Anzeige selbst saniert react-letter). */
function stripHtml(html: string | null | undefined): string {
  if (!html) return "";
  return html
    .replace(/<(script|style)[^>]*>[\s\S]*?<\/\1>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function grossAnfang(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Einzeilige Zeile einer aelteren Nachricht („Ristorante Storia · Betreff · Mo., 19.10. 20:01“). */
export function MailKurzZeile({ email, kontext, onAufklappen }: { email: GespraechEmailRow; kontext: MailKontext; onAufklappen: () => void }) {
  const art = email.art === "betrieb" ? "wir" : seite(email);
  const name = anzeigeName(email, kontext);
  const betreff = dekodiereMailBetreff(email.subject)?.trim();
  const status = mailStatus(email);
  return (
    <button
      type="button"
      data-testid={`gespraech-kurzzeile-${email.id}`}
      aria-expanded="false"
      onClick={onAufklappen}
      className="flex w-full items-center gap-3 border-b border-outline-variant/25 px-3.5 py-3 text-left text-on-surface-variant hover:bg-surface-container-high/50 dark:border-outline/15 dark:hover:bg-white/[0.03]"
    >
      <Avatar name={name} art={art} klein />
      <span className="min-w-0 flex-1">
        <span className="block truncate font-body-sm text-[14.5px] font-bold text-on-background dark:text-inverse-on-surface">{name}</span>
        <span className="block truncate font-body-sm text-[13px] text-secondary dark:text-secondary-fixed-dim">
          {betreff ?? vorschau(email.bodyText, 80)}
          {status?.ton === "err" && <span className={` ${TON_KLASSE.err}`}> · Nicht zugestellt</span>}
        </span>
      </span>
      <span className="shrink-0 font-body-sm text-[12.5px] text-secondary dark:text-secondary-fixed-dim">{kurzesDatum(sendezeitpunkt(email))}</span>
    </button>
  );
}

/** Eine geoeffnete Nachricht. */
export function MailNachricht({
  email,
  kontext,
  unbeantwortet,
  entwurfLaeuft,
  onAntworten,
  onKiEntwurf,
  onZuordnungGeaendert,
  onZuklappen,
}: {
  email: GespraechEmailRow;
  kontext: MailKontext;
  unbeantwortet: string | null;
  entwurfLaeuft: boolean;
  /** `undefined`, wenn hier nicht geantwortet werden kann (Betrieb-Reiter). */
  onAntworten?: () => void;
  onKiEntwurf?: () => void;
  onZuordnungGeaendert: () => void;
  onZuklappen?: () => void;
}) {
  const istGast = email.direction === "inbound";
  const istBetriebsHinweis = email.art === "betrieb";
  const art = istBetriebsHinweis ? "wir" : seite(email);
  const name = anzeigeName(email, kontext);
  const istZuordenbar = istGast && email.id !== SYNTHETISCHE_NACHRICHT_ID;
  const [aendernOffen, setAendernOffen] = useState(false);
  const [aufhebenOffen, setAufhebenOffen] = useState(false);
  const zeit = sendezeitpunkt(email);
  const status = mailStatus(email);
  const ereignisText = formatMailEreignisse(email.ereignisse);
  const statusText = status?.ton === "err" ? status.text : ereignisText ? grossAnfang(ereignisText) : (status?.text ?? null);
  const statusTon: StatusTon = unbeantwortet ? "amb" : (status?.ton ?? "neutral");
  const anhaenge = email.attachmentsManifest ?? [];
  const absender = adresseVon(email.fromAddr);
  const empfaenger = email.empfaenger && email.empfaenger.length > 0 ? email.empfaenger : email.toAddr ? [email.toAddr] : [];
  const anZeile: ReactNode = istGast
    ? `An: ${kontext.firmaName?.trim() || "Betrieb"}${email.channel === "whatsapp" ? " · per WhatsApp" : " · über die Gastseite"}`
    : `An: ${empfaenger
        .map((a) => (kontext.kundenName && !istBetriebsHinweis ? `${kontext.kundenName} <${adresseVon(a)}>` : adresseVon(a)))
        .join(", ")}`;

  return (
    <article data-testid={`gespraech-nachricht-${email.id}`} className="border-b-8 border-surface-container-high/70 px-3.5 pb-3 pt-3.5 dark:border-white/[0.05]">
      <header className="flex items-start gap-3">
        <Avatar name={name} art={art} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-1.5 font-body-sm text-[15.5px] font-bold text-on-background dark:text-inverse-on-surface">
            <span>{name}</span>
            <span
              className={`rounded-[5px] px-1.5 py-px align-[2px] text-[11px] font-bold tracking-[0.04em] ${
                art === "gast" ? "bg-slate-100 text-slate-600 dark:bg-slate-700/40 dark:text-slate-300" : "bg-primary/10 text-primary"
              }`}
            >
              {art === "gast" ? "GAST" : istBetriebsHinweis ? "HINWEIS" : "DU"}
            </span>
            {email.channel === "whatsapp" && <WhatsappKennzeichen />}
          </div>
          {absender && <div className="break-all font-body-sm text-[13px] text-secondary dark:text-secondary-fixed-dim">{absender}</div>}
          <div className="break-words font-body-sm text-[13px] text-on-surface-variant">{anZeile}</div>
        </div>
        <div className="shrink-0 text-right font-body-sm text-[13px] text-secondary dark:text-secondary-fixed-dim">
          {onZuklappen ? (
            <button type="button" onClick={onZuklappen} aria-label="Nachricht einklappen" className="hover:text-primary">
              {langesDatum(zeit)}
            </button>
          ) : (
            langesDatum(zeit)
          )}
        </div>
      </header>

      <div className="pb-1 pt-3">
        <GespraechMailKoerper email={email} />
      </div>

      {email.error && (
        <div className="mt-2">
          <MailFehlerHinweis rawError={email.error} />
        </div>
      )}
      {anhaenge.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {anhaenge.map((a, i) => (
            <AnhangChip key={i} a={a} />
          ))}
        </div>
      )}

      {(unbeantwortet || statusText) && (
        <p data-testid={`gespraech-zustellstatus-${email.id}`} className={`mt-2 font-body-sm text-[13px] font-semibold ${TON_KLASSE[statusTon]}`}>
          {unbeantwortet ?? statusText}
        </p>
      )}

      <div className="mt-2.5 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          {istZuordenbar && (
            <>
              <ZuordnungsChip messageId={email.id} />
              <NachrichtMenu onAendern={() => setAendernOffen(true)} onAufheben={() => setAufhebenOffen(true)} />
            </>
          )}
        </div>
        {istGast && (onKiEntwurf || onAntworten) && (
          <div className="flex items-center gap-5 text-primary">
            {onKiEntwurf && (
              <button
                type="button"
                data-testid={`gespraech-ki-entwurf-${email.id}`}
                disabled={entwurfLaeuft}
                onClick={onKiEntwurf}
                className="inline-flex items-center gap-1 font-body-sm text-[13px] font-semibold hover:underline disabled:opacity-50"
              >
                <Icon name="auto_awesome" className="text-[18px]" />
                {entwurfLaeuft ? "Entwirft …" : "KI-Entwurf"}
              </button>
            )}
            {onAntworten && (
              <button type="button" data-testid={`gespraech-antworten-${email.id}`} onClick={onAntworten} className="inline-flex items-center gap-1 font-body-sm text-[13px] font-semibold hover:underline">
                <Icon name="reply" className="text-[18px]" />
                Antworten
              </button>
            )}
          </div>
        )}
      </div>

      {istZuordenbar && (
        <>
          <ZuordnungAendernModal open={aendernOffen} onClose={() => setAendernOffen(false)} messageId={email.id} aktuellerEventId={kontext.eventId} onErfolg={onZuordnungGeaendert} />
          <ZuordnungAufhebenModal open={aufhebenOffen} onClose={() => setAufhebenOffen(false)} messageId={email.id} onErfolg={onZuordnungGeaendert} />
        </>
      )}
    </article>
  );
}
